import React, { useState } from 'react';
import {
  Box,
  Dialog,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  DeleteOutline,
  GppMaybeOutlined,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { authColors, fontStack } from './auth/authStyles';

const DeleteAccount = () => {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { logout, token } = useAuth();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  const handleOpen = () => {
    setOpen(true);
    setError('');
    setConfirmText('');
  };

  const handleClose = () => {
    setOpen(false);
    setError('');
    setConfirmText('');
  };

  const handleDelete = async () => {
    if (confirmText !== 'SUPPRIMER') {
      setError('Veuillez taper "SUPPRIMER" pour confirmer');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const authToken = token || localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/auth/delete-account`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
        withCredentials: true,
      });

      handleClose();
      window.localStorage.setItem('accountDeleted', 'true');
      logout();
    } catch (err) {
      console.error('Erreur suppression:', err);
      setError(err.response?.data?.error || 'Erreur lors de la suppression du compte');
    } finally {
      setLoading(false);
    }
  };

  const isConfirmed = confirmText === 'SUPPRIMER';

  return (
    <>
      {/* Bouton de suppression (style maquette) */}
      <Box
        component="button"
        type="button"
        onClick={handleOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          border: 'none',
          cursor: 'pointer',
          borderRadius: '10px',
          backgroundColor: authColors.danger,
          color: '#FFFFFF',
          fontFamily: fontStack,
          fontSize: '13px',
          fontWeight: 700,
          padding: '10px 16px',
          flexShrink: 0,
          transition: 'filter 0.15s ease',
          '&:hover': { filter: 'brightness(1.08)' },
        }}
      >
        <DeleteOutline sx={{ fontSize: 16 }} />
        Supprimer
      </Box>

      {/* Modal de confirmation */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: authColors.bgCard,
            border: `1px solid ${authColors.borderSubtle}`,
            borderRadius: '16px',
            boxShadow: '0 16px 40px rgba(0, 0, 0, 0.42)',
            p: '22px',
            m: 2,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* En-tête */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '999px',
                backgroundColor: '#4A1B24',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <GppMaybeOutlined sx={{ fontSize: 20, color: authColors.danger }} />
            </Box>
            <Typography sx={{ color: authColors.textPrimary, fontSize: '15px', fontWeight: 700, fontFamily: fontStack }}>
              Confirmer la suppression
            </Typography>
          </Box>

          <Typography sx={{ color: authColors.textMuted, fontSize: '12px', lineHeight: 1.5, fontFamily: fontStack }}>
            Cette action est définitive. Toutes vos données, alertes et permissions
            seront effacées. Tapez <strong style={{ color: authColors.danger }}>SUPPRIMER</strong> pour confirmer.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ borderRadius: '10px' }}>
              {error}
            </Alert>
          )}

          {/* Champ de confirmation */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '42px',
              backgroundColor: authColors.bgInput,
              borderRadius: '10px',
              border: `1px solid ${authColors.danger}`,
              px: '14px',
            }}
          >
            <Box
              component="input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="SUPPRIMER"
              autoComplete="off"
              sx={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: authColors.danger,
                fontFamily: fontStack,
                fontSize: '13px',
                fontWeight: 700,
                letterSpacing: '1.5px',
                '&::placeholder': { color: authColors.textPlaceholder, letterSpacing: '1.5px' },
              }}
            />
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: '10px' }}>
            <Box
              component="button"
              type="button"
              onClick={handleClose}
              disabled={loading}
              sx={{
                flex: 1,
                height: '42px',
                border: `1px solid ${authColors.borderSubtle}`,
                cursor: loading ? 'not-allowed' : 'pointer',
                borderRadius: '10px',
                backgroundColor: authColors.bgInput,
                color: authColors.textPrimary,
                fontFamily: fontStack,
                fontSize: '13px',
                fontWeight: 600,
              }}
            >
              Annuler
            </Box>
            <Box
              component="button"
              type="button"
              onClick={handleDelete}
              disabled={loading || !isConfirmed}
              sx={{
                flex: 1,
                height: '42px',
                border: 'none',
                cursor: loading || !isConfirmed ? 'not-allowed' : 'pointer',
                borderRadius: '10px',
                backgroundColor: authColors.danger,
                color: '#FFFFFF',
                fontFamily: fontStack,
                fontSize: '13px',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: loading || !isConfirmed ? 0.5 : 1,
              }}
            >
              {loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <DeleteOutline sx={{ fontSize: 16 }} />}
              {loading ? 'Suppression...' : 'Supprimer'}
            </Box>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default DeleteAccount;
